import { Button, ButtonMode, Divider, Icon, ICONS, Modal, ModalWidth, ToasterMode } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import { difference } from 'lodash';
import intersection from 'lodash/intersection';
import isEmpty from 'lodash/isEmpty';
import React, { useEffect, useState } from 'react';
import { GENERIC_ERROR_TEXT } from '../../../../Base.constants';
import { useGlobalModalContext } from '../../../../components/GlobalModal';
import useMatchIssuersByTerms from '../../../../hooks/issuers/useMatchIssuersByTerms';
import { useToast } from '../../../../hooks/toast/useToast';
import { useUpdateWatchList } from '../../../../hooks/watchLists';
import { WatchList } from '../../../../models/WatchList';
import { ISSUERS_LIMIT_PER_USER, ISSUERS_LIMIT_PER_WATCHLIST } from '../Watchlist/Watchlist.logic';
import UploadFile, { File } from './UploadFile/UploadFile';
import { downloadTemplate, readXlsx } from './UploadIssuersModal.logic';

import styles from './UploadIssuersModal.module.scss';

const cx = classNames.bind(styles);

interface UploadIssuersModalProps {
  watchList: WatchList;
  totalTagsCount: number;
}

const UploadIssuersModal = ({ watchList, totalTagsCount }: UploadIssuersModalProps): React.ReactElement => {
  const { closeModal } = useGlobalModalContext();
  const { showToast } = useToast();

  const [file, setFile] = useState<File | null>();
  const [tickers, setTickers] = useState<string[] | null>(null);
  const [isUploadError, setIsUploadError] = useState(false);
  const [isFileLoading, setIsFileLoading] = useState(false);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);

  const {
    mutateAsync: matchIssuers,
    isLoading: isMatchIssuersLoading,
    isError: isMatchingError,
  } = useMatchIssuersByTerms();
  const { mutate: updateWatchlist, isLoading: isUpdatingWatchlist, isError: isUpdatingError } = useUpdateWatchList();

  useEffect(() => {
    if (isMatchingError || isUpdatingError) {
      closeModal();
      showToast('Upload failed. Please try again.', ToasterMode.ERROR);
    }
  }, [isMatchingError, isUpdatingError]);

  const onAddToWatchList = async () => {
    const { matchedTags } = await matchIssuers(tickers!);
    let tagsToAdd = difference(matchedTags, watchList.tags);

    if (isEmpty(tagsToAdd) && !isEmpty(matchedTags)) {
      closeModal();
      showToast('One or more issuers in the file are already selected in the watchlist. These will not be added again');
      return;
    }

    const fileHasExistingTags = intersection(matchedTags, watchList.tags).length > 0;
    let toastMessage = fileHasExistingTags
      ? 'One or more issuers in the file are already selected in the watchlist. These will not be added again'
      : `${tagsToAdd.length} issuers have been added successfully`;

    const currentCount = watchList.tags.length;
    const isWatchlistLimitExceeded = currentCount + tagsToAdd.length > ISSUERS_LIMIT_PER_WATCHLIST;
    const isUserLimitExceeded = totalTagsCount + tagsToAdd.length > ISSUERS_LIMIT_PER_USER;

    if (isWatchlistLimitExceeded && !isUserLimitExceeded) {
      tagsToAdd = tagsToAdd.slice(0, ISSUERS_LIMIT_PER_WATCHLIST - currentCount);
      toastMessage = `There is a maximum of ${ISSUERS_LIMIT_PER_WATCHLIST} issuers per Watchlist. Remove existing issuers to add new ones.`;
    } else if (isUserLimitExceeded) {
      tagsToAdd = tagsToAdd.slice(0, ISSUERS_LIMIT_PER_USER - totalTagsCount);
      toastMessage = `There is a maximum of ${ISSUERS_LIMIT_PER_USER} issuers across all of your watchlists. Remove existing issuers to add new ones.`;
    }

    updateWatchlist(
      {
        id: watchList.id,
        watchList: {
          ...watchList,
          tags: watchList.tags.concat(tagsToAdd),
        },
      },
      {
        onSuccess: () => {
          closeModal();
          showToast(toastMessage);
        },
      }
    );
  };

  const onDownloadTemplate = async () => {
    try {
      setIsDownloadingTemplate(true);
      await downloadTemplate();
    } catch (e) {
      showToast(GENERIC_ERROR_TEXT, ToasterMode.ERROR);
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  const renderFooter = () => (
    <>
      <Button
        dataTestId="download-template-button"
        mode={ButtonMode.TERTIARY}
        onClick={onDownloadTemplate}
        loading={isDownloadingTemplate}
      >
        Download template
      </Button>
      <Button
        mode={ButtonMode.PRIMARY}
        disabled={isEmpty(tickers)}
        loading={isMatchIssuersLoading || isUpdatingWatchlist}
        onClick={onAddToWatchList}
        dataTestId="upload-issuers-confirm"
      >
        Add to watchlist
      </Button>
    </>
  );

  const reset = () => {
    setFile(null);
    setTickers(null);
  };

  const parseFile = (file: File) => {
    // readXlsx is blocking operation therefore we start file parsing in a timeout to "ensure" that loader had time to be displayed.
    setTimeout(() => {
      try {
        const tickers = readXlsx(file);
        setTickers(tickers);
        setFile(file);
        setIsUploadError(false);
      } catch (e) {
        setIsUploadError(true);
      } finally {
        setIsFileLoading(false);
      }
    }, 100);
  };

  const onUploadError = () => {
    setIsUploadError(true);
    setIsFileLoading(false);
  };

  const renderDragAndDrop = () => {
    return (
      <UploadFile
        onSuccess={parseFile}
        onError={onUploadError}
        isError={isUploadError}
        isLoading={isFileLoading}
        setIsLoading={setIsFileLoading}
      />
    );
  };

  const renderUploadedFile = () => {
    return (
      <div className={cx('uploaded-file')}>
        <div className={cx('file')}>
          <div className={cx('name-container')}>
            <Icon name={file!.fileIcon} width={25} height={32} dataTestId={file!.fileIcon} />
            <div data-testid="fileName">
              <div className={cx('name')}>{file!.name}</div>
            </div>
          </div>
          <div className={cx('action')}>
            <Divider className={cx('divider')} vertical />
            <Button
              mode={ButtonMode.PLAIN}
              ariaLabel="clear file"
              className={cx('clear-button')}
              onClick={reset}
              disabled={isMatchIssuersLoading || isUpdatingWatchlist}
            >
              <Icon name={ICONS.Times} />
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Modal
      width={ModalWidth.Medium}
      headerTitle="Upload companies"
      className={cx('body')}
      onClose={closeModal}
      footer={renderFooter()}
      open
      dataTestId="upload-issuers-modal"
    >
      <>
        {!tickers && renderDragAndDrop()}
        {tickers && renderUploadedFile()}
      </>
    </Modal>
  );
};

export default UploadIssuersModal;
